import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getLatestPosts } from '../blog/manifest-latest.js';
import { useLocalizedPath } from '../hooks/useLocalizedPath.jsx';
import { HeroBadge } from './sections/HeroBadge.jsx';
import { ArrowIcon } from './sections/ArrowIcon.jsx';
import { INK, PURPLE, SURFACE, gridBg } from './sections/theme.js';
import { BlogPostCard } from './BlogPostCard.jsx';

export default function LatestBlogArticles() {
    const { t, i18n } = useTranslation();
    const { blog } = useLocalizedPath();
    const articles = useMemo(() => getLatestPosts(i18n.language), [i18n.language]);

    return (
        <Box
            component="section"
            sx={{
                ...gridBg(SURFACE.lavender),
                py: { xs: 6, md: 9 },
                borderTop: '1px solid rgba(26,23,38,0.06)',
                borderBottom: '1px solid rgba(26,23,38,0.06)',
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{
                        alignItems: { xs: 'flex-start', md: 'flex-end' },
                        justifyContent: 'space-between',
                        mb: 5,
                    }}
                >
                    <Box>
                        <HeroBadge label={t('blog.latest_articles.tag')} />
                        <Typography
                            component="h2"
                            sx={{
                                fontSize: { xs: '1.75rem', md: '2.125rem' },
                                fontWeight: 700,
                                lineHeight: 1.2,
                                letterSpacing: '-0.01em',
                                color: INK,
                            }}
                        >
                            {t('blog.latest_articles.title')}
                        </Typography>
                    </Box>
                    <Stack
                        component={Link}
                        to={blog()}
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            gap: 0.75,
                            flexShrink: 0,
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            color: PURPLE,
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': { color: '#3F1C84' },
                        }}
                    >
                        {t('home.purpose-button')}
                        <ArrowIcon />
                    </Stack>
                </Stack>
                <Grid container spacing={3}>
                    {articles.map((article) => (
                        <Grid key={article.slug} size={{ xs: 12, sm: 6, md: 4 }}>
                            <BlogPostCard post={article} lang={i18n.language} titleComponent="h3" showDate={false} />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}
